// Work mode (`/c/{slug}/work`; spec 06, design spec §4; M2-07; Work UX pass 2026-10-20): `/work`
// lands on the board you opened last, else on My tasks (`/work/my`); Boards (`/work/boards`, was `/work/home`) is the
// overview, a space (`/work/s/{id}`) its open tasks by board, a board (`/work/b/{id}?view=`) its
// saved views, a page (`/work/p/{id}`; M7-02) its editor. The open task is in the URL (`?task=`)
// on any of them, so a reload or a shared link opens the drawer over the same page. M7-04 adds
// Memory review (`/work/memory/review`); DOCS-KB, Docs (`/work/docs?view=`). The mode's ModeGate keeps role and add-on rules;
// `WorkLive` shows one honest page on an API that does not serve boards yet.
import { PageFrame, Skeleton } from "@companyos/ui";
import { useQuery } from "@tanstack/react-query";
import { createRoute, Outlet, redirect, useNavigate } from "@tanstack/react-router";
import { useCallback, useEffect } from "react";
import {
  MEMORY_TABS,
  MemoryReviewScreen,
  type MemoryTab,
} from "../features/memory/memory-review-screen";
import { useCompany, useFeature } from "../features/use-feature";
import { spaceOpening } from "../features/work/board-opening";
import { boardsOf } from "../features/work/board-order";
import { BoardScreen, type ViewKind } from "../features/work/board-screen";
import { DOCS_VIEWS, DocsScreen, type DocsView } from "../features/work/docs-screen";
import { PageScreen } from "../features/work/page-screen";
import { TaskDrawer } from "../features/work/task-drawer";
import { TaskSearchScreen } from "../features/work/task-search-screen";
import { VIEW_KINDS } from "../features/work/work-dialogs";
import { WorkHome } from "../features/work/work-home";
import { workLanding } from "../features/work/work-landing";
import { boardsQuery, spacesQuery } from "../features/work/work-queries";
import { MyTasks, SpaceHome, WorkLive } from "../features/work/work-screens";
import { validateFocusSearch } from "../focus/focus";
import { NotEnabledPage } from "../pages/status-pages";
import { ModeGate } from "../shell/mode-gate";
import { companyRoute } from "./c.$slug";

const ID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** Opens (or closes, with `null`) the task drawer, keeping the rest of the URL. */
function useOpenTask() {
  const navigate = useNavigate();
  return (id: string | null) =>
    void navigate({
      to: ".",
      search: (prev: Record<string, unknown>) => {
        const { task: _drop, ...rest } = prev;
        return id ? { ...rest, task: id } : rest;
      },
    } as never);
}

export const workRoute = createRoute({
  getParentRoute: () => companyRoute,
  path: "work",
  validateSearch: (search: Record<string, unknown>) => ({
    ...validateFocusSearch(search),
    ...(typeof search.task === "string" && ID.test(search.task) ? { task: search.task } : {}),
  }),
  component: function Work() {
    const { api } = workRoute.useRouteContext();
    const { task } = workRoute.useSearch();
    const company = useCompany();
    const openTask = useOpenTask();
    return (
      // The full task page fills the main region (its composer sits at the bottom).
      <ModeGate modeKey="work" className="[&:has(>[data-screen=task-page])]:h-full">
        <WorkLive api={api} company={company}>
          <Outlet />
          <TaskDrawer
            api={api}
            company={company}
            taskId={task ?? null}
            onClose={() => openTask(null)}
            onOpenTask={openTask}
          />
        </WorkLive>
      </ModeGate>
    );
  },
});

/** `/work`: straight to the work (the last board, else My tasks); the search (`task`, focus) carries. */
export const workIndexRoute = createRoute({
  getParentRoute: () => workRoute,
  path: "/",
  beforeLoad: ({ params, search }) => {
    const landing = workLanding(params.slug);
    throw redirect({
      to: landing.to,
      params:
        landing.to === "/c/$slug/work/b/$boardId"
          ? { ...params, boardId: landing.boardId }
          : params,
      search: search as never,
      replace: true,
    } as never);
  },
});

export const workBoardsRoute = createRoute({
  getParentRoute: () => workRoute,
  path: "boards",
  component: function WorkBoardsRoute() {
    const { api } = workBoardsRoute.useRouteContext();
    const openTask = useOpenTask();
    return <WorkHome api={api} company={useCompany()} onOpenTask={openTask} />;
  },
});

/**
 * SHELL-DECISIONS (2026-10-30): Work's overview is "Boards"; its old URLs (`/work/home`, and
 * `/work/desk` for a day) → `/work/boards`, the raw query (not the validated one, which may drop
 * keys) and the hash kept. Only the rail's page is called Desk.
 */
const oldBoardsPath = (path: "home" | "desk") =>
  createRoute({
    getParentRoute: () => workRoute,
    path,
    beforeLoad: ({ params, location }) => {
      throw redirect({
        to: "/c/$slug/work/boards",
        params,
        search: location.search as never,
        hash: location.hash || undefined,
        replace: true,
      } as never);
    },
  });
export const workHomeRoute = oldBoardsPath("home");
export const workDeskRoute = oldBoardsPath("desk");

export const workMyRoute = createRoute({
  getParentRoute: () => workRoute,
  path: "my",
  component: function WorkMy() {
    const { api } = workMyRoute.useRouteContext();
    const { task } = workMyRoute.useSearch();
    const openTask = useOpenTask();
    return <MyTasks api={api} company={useCompany()} taskId={task} onOpenTask={openTask} />;
  },
});

/** T5-TASK-SEARCH: Search tasks (`?q=` keeps the words, so a link reopens the same search). */
export const workSearchRoute = createRoute({
  getParentRoute: () => workRoute,
  path: "search",
  validateSearch: (search: Record<string, unknown>) =>
    typeof search.q === "string" && search.q.length <= 500 ? { q: search.q } : {},
  component: function WorkSearch() {
    const { api } = workSearchRoute.useRouteContext();
    const { q } = workSearchRoute.useSearch();
    const openTask = useOpenTask();
    const navigate = useNavigate();
    const onQuery = useCallback(
      (next: string) =>
        void navigate({
          to: ".",
          replace: true,
          search: (prev: Record<string, unknown>) => {
            const { q: _drop, ...rest } = prev;
            return next ? { ...rest, q: next } : rest;
          },
        } as never),
      [navigate],
    );
    return (
      <TaskSearchScreen
        api={api}
        company={useCompany()}
        initialQuery={q ?? ""}
        onQuery={onQuery}
        onOpenTask={openTask}
      />
    );
  },
});

/**
 * BOARD-DEFAULT-VIEW: a space opens what its settings say (`opens_on`), else its first board, on
 * that board's default view (a replace, no new history entry). `?view=overview` (the board's
 * space crumb) or a space set to open on its overview shows the overview: its open tasks by
 * board.
 */
export const workSpaceRoute = createRoute({
  getParentRoute: () => workRoute,
  path: "s/$spaceId",
  validateSearch: (search: Record<string, unknown>) => ({
    ...(search.view === "overview" ? { view: "overview" as const } : {}),
  }),
  component: function WorkSpace() {
    const { api } = workSpaceRoute.useRouteContext();
    const { spaceId } = workSpaceRoute.useParams();
    const { task, view } = workSpaceRoute.useSearch();
    const company = useCompany();
    const navigate = useNavigate();
    const openTask = useOpenTask();
    const spaces = useQuery(spacesQuery(api, company.id));
    const boards = useQuery(boardsQuery(api, company.id));
    const space = spaces.data?.data.find((s) => s.id === spaceId);
    const opening =
      space && boards.data
        ? spaceOpening(space.settings.opens_on, boardsOf(boards.data.data, spaceId), view)
        : null;
    const boardId = opening && opening !== "overview" ? opening.board : null;
    useEffect(() => {
      if (!boardId) return;
      void navigate({
        to: "/c/$slug/work/b/$boardId",
        params: { slug: company.slug, boardId },
        search: (prev: Record<string, unknown>) => {
          const { view: _drop, ...rest } = prev;
          return rest;
        },
        replace: true,
      } as never);
    }, [boardId, company.slug, navigate]);
    if (boardId || (view !== "overview" && (spaces.isPending || boards.isPending)))
      return (
        <div role="status" aria-label="Loading the space" className="flex flex-col gap-3 px-6 py-4">
          <Skeleton className="h-9 w-1/3" />
          <Skeleton className="h-64 w-full" />
        </div>
      );
    return (
      <SpaceHome
        key={spaceId}
        api={api}
        company={company}
        spaceId={spaceId}
        taskId={task}
        onOpenTask={openTask}
      />
    );
  },
});

export const workBoardRoute = createRoute({
  getParentRoute: () => workRoute,
  path: "b/$boardId",
  validateSearch: (search: Record<string, unknown>) => ({
    ...(typeof search.view === "string" && ID.test(search.view) ? { view: search.view } : {}),
    // WORK-OS: the active view shown as another kind (a Board/List/… tab without a saved view).
    ...(typeof search.kind === "string" && VIEW_KINDS.some((k) => k.value === search.kind)
      ? { kind: search.kind as ViewKind }
      : {}),
  }),
  component: function WorkBoard() {
    const { api } = workBoardRoute.useRouteContext();
    const { boardId } = workBoardRoute.useParams();
    const { task, view, kind } = workBoardRoute.useSearch();
    const navigate = useNavigate();
    const openTask = useOpenTask();
    const onOpened = useCallback(
      (opened: { view: string } | { kind: ViewKind }) =>
        void navigate({
          to: ".",
          replace: true,
          search: (prev: Record<string, unknown>) => ({ ...prev, ...opened }),
        } as never),
      [navigate],
    );
    return (
      <BoardScreen
        key={boardId}
        api={api}
        company={useCompany()}
        boardId={boardId}
        viewId={view}
        kind={kind}
        taskId={task}
        onOpenTask={openTask}
        // BOARD-DEFAULT-VIEW: switching views replaces the entry (no history spam).
        onView={(id) =>
          void navigate({
            to: ".",
            replace: true,
            search: (prev: Record<string, unknown>) => {
              const { kind: _drop, ...rest } = prev;
              return { ...rest, view: id };
            },
          } as never)
        }
        onKind={(k) =>
          void navigate({
            to: ".",
            replace: true,
            search: (prev: Record<string, unknown>) => {
              const { kind: _drop, ...rest } = prev;
              return k ? { ...rest, kind: k } : rest;
            },
          } as never)
        }
        onOpened={onOpened}
      />
    );
  },
});

/**
 * M7-04: Memory review (`/work/memory/review?tab=`), on the `memory` add-on: "not enabled" on a
 * direct URL while it is off.
 */
export const workMemoryReviewRoute = createRoute({
  getParentRoute: () => workRoute,
  path: "memory/review",
  validateSearch: (search: Record<string, unknown>) => ({
    ...(typeof search.tab === "string" && (MEMORY_TABS as readonly string[]).includes(search.tab)
      ? { tab: search.tab as MemoryTab }
      : {}),
    // INBOX-READABLE: `?scopeId=<id>` — the inbox's Review on one memory scope's row (`scope` is
    // the shell's focus key).
    ...(typeof search.scopeId === "string" && /^[0-9a-f-]{36}$/i.test(search.scopeId)
      ? { scopeId: search.scopeId }
      : {}),
  }),
  component: function WorkMemoryReview() {
    const { api } = workMemoryReviewRoute.useRouteContext();
    const { tab, scopeId } = workMemoryReviewRoute.useSearch();
    const company = useCompany();
    const memory = useFeature("memory");
    const navigate = useNavigate();
    if (!memory.enabled)
      return (
        <NotEnabledPage
          what="Memory review"
          feature="memory"
          companyName={company.name}
          slug={company.slug}
        />
      );
    return (
      <MemoryReviewScreen
        api={api}
        company={company}
        tab={tab ?? "review"}
        scope={scopeId ?? null}
        onClearScope={() =>
          void navigate({
            to: ".",
            search: (prev: Record<string, unknown>) => {
              const { scopeId: _drop, ...rest } = prev;
              return rest;
            },
          } as never)
        }
        onTab={(t) =>
          void navigate({
            to: ".",
            search: (prev: Record<string, unknown>) => {
              const { tab: _drop, ...rest } = prev;
              return t === "review" ? rest : { ...rest, tab: t };
            },
          } as never)
        }
      />
    );
  },
});

/**
 * DOCS-KB: Docs (`/work/docs?view=`), every doc across the spaces. WORK-OS: `?space=` opens it
 * filtered to one space (the sidebar's Docs row under a space).
 */
export const workDocsRoute = createRoute({
  getParentRoute: () => workRoute,
  path: "docs",
  validateSearch: (search: Record<string, unknown>) => ({
    ...(typeof search.view === "string" && (DOCS_VIEWS as readonly string[]).includes(search.view)
      ? { view: search.view as DocsView }
      : {}),
    ...(typeof search.space === "string" && ID.test(search.space) ? { space: search.space } : {}),
  }),
  component: function WorkDocs() {
    const { api } = workDocsRoute.useRouteContext();
    const { view, space } = workDocsRoute.useSearch();
    const navigate = useNavigate();
    return (
      <DocsScreen
        key={space ?? ""}
        api={api}
        company={useCompany()}
        view={view ?? "all"}
        spaceId={space}
        onView={(v) =>
          void navigate({
            to: ".",
            search: (prev: Record<string, unknown>) => {
              const { view: _drop, ...rest } = prev;
              return v === "all" ? rest : { ...rest, view: v };
            },
          } as never)
        }
      />
    );
  },
});

export const workPageRoute = createRoute({
  getParentRoute: () => workRoute,
  path: "p/$pageId",
  component: function WorkPage() {
    const { api } = workPageRoute.useRouteContext();
    const { pageId } = workPageRoute.useParams();
    // Work is full-bleed (boards and lists); a page is a document, so it gets the document frame
    // (padding and max width) like every other document screen.
    return (
      <PageFrame width="doc" className="min-h-full">
        <PageScreen key={pageId} api={api} company={useCompany()} pageId={pageId} />
      </PageFrame>
    );
  },
});
